import { ForbiddenError, NotFoundError } from '@ember-data/adapter/error';
import { action } from '@ember/object';
import Route from '@ember/routing/route';
import { service } from '@ember/service';

import window from 'ember-window-mock';

import { embedOnboardingURL } from 'register-partners-js/constants/hosts';
import { shouldRedirectToEmbed } from 'register-partners-js/utils/should-redirect-to-embed';

const POSSIBLE_STATUSES_AFTER_CONTRACT_SIGNED = ['completed', 'contract_signed'];

export default class RegistrationRoute extends Route {
  @service segment;
  @service store;
  @service currentUser;
  @service session;
  @service zendeskLocalization;
  @service zendeskWidget;
  @service launchdarkly;
  @service apiFlowManager;

  async model(params) {
    try {
      let registration = await this.store.findRecord('registration', params.registration_id, {
        include: 'stakeholders',
        reload: true,
      });
      // The organization returned from the above endpoint using `include=organizations`,
      // doesn't contain the `features` array.
      // We therefore have to fetch the organization in an additional API request,
      // which actually contains the `features` array.
      // Note: We currently don't have the organizations/:id endpoint, therefore, we have to fetch all organizations
      await this.store.findAll('organization');

      return registration;
    } catch (error) {
      if (error instanceof NotFoundError) {
        this.apiFlowManager.isPartnersService = false;
        let registration = await this.store.findRecord(
          'partner-registration',
          params.registration_id,
          {
            reload: true,
          }
        );

        let organization = await registration.get('organization');
        await organization.owner;

        return registration;
      } else {
        throw error;
      }
    }
  }

  async afterModel(registration) {
    let legalCountry = registration.get('organization.legalCountry');
    let phoneNumber = registration.get('owner.phoneNumber');
    this.segment.identify({ phoneNumber });
    this.zendeskLocalization.organizationCountry = legalCountry;
    this.zendeskWidget.selectCountry(legalCountry);

    // If this is an Embed registration, and it is marked as completed or contract_signed, we send the user to the Embed
    // flow. It might happen that the registration is marked as complete, but it is still pending the embed related steps.
    // If it turns out the embed steps are also done, the embed app will ultimately redirect the user to the appropriate
    // destination.
    let organization = await registration.get('organization');
    if (
      shouldRedirectToEmbed(registration, organization) &&
      POSSIBLE_STATUSES_AFTER_CONTRACT_SIGNED.includes(registration.status)
    ) {
      window.location.replace(`${embedOnboardingURL}/${registration.id}`);
    }
  }

  @action
  error(error) {
    /**
     * This case is most likely never happening in production:
     * If a user tries to access someone else's registration
     * they get logged out
     */
    if (error instanceof ForbiddenError) {
      this.session.invalidate();
      return false;
    }
    return true;
  }
}
